// «Антифрод» в настройках (этап 6): каждый лид наших лендингов получает риск 0–100 по признакам.
// По умолчанию риск только помечает лид. Порог задержки включает владелец: лиды от порога не уходят ни в сеть,
// ни в Meta, пока он не решит в карточке лида. Ниже — как сеть решала лиды каждого уровня: проверка, что риск не врёт.
import { Card, Segmented, toast } from '@autoaff/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { api } from '../api';
import { capFirst, FRAUD_LEVEL, type FraudLevel } from '../business';

interface AntifraudView {
  holdAt: number | null;
  held: number;
  thresholds: { medium: number; high: number };
  levels: Array<{ level: FraudLevel; leads: number; approved: number; rejected: number; trash: number }>;
}

const HOLD = [
  { value: 'off', label: 'Не задерживать' },
  { value: '50', label: 'От 50' },
  { value: '70', label: 'От 70' },
  { value: '90', label: 'От 90' },
] as const;
type Hold = (typeof HOLD)[number]['value'];

const share = (n: number, of: number) => (of > 0 ? `${((n / of) * 100).toFixed(1)}%` : '—');

export function AntifraudCard() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['antifraud'], queryFn: () => api<AntifraudView>('/api/leads/antifraud') });
  const d = q.data;
  const hold: Hold = d?.holdAt
    ? ((HOLD.find((h) => h.value === String(d.holdAt))?.value ?? '50') as Hold)
    : 'off';

  const save = async (v: Hold) => {
    try {
      const holdAt = v === 'off' ? null : Number(v);
      await api('/api/leads/antifraud', { holdAt }, 'PUT');
      qc.setQueryData<AntifraudView>(['antifraud'], (old) => (old ? { ...old, holdAt } : old));
      toast(holdAt ? `Лиды с риском от ${holdAt} будут ждать вашего решения` : 'Риск только помечает лиды');
    } catch (e) {
      toast((e as Error).message);
    }
  };

  return (
    <Card title="Антифрод">
      <p className="muted af-lead">
        Каждый лид с наших лендингов получает риск от 0 до 100: бот, форма за секунду, номер-шаблон, мусор в
        имени, несколько номеров с одного телефона. По умолчанию риск только помечает лид — в сеть уходят все.
      </p>
      <div className="af-hold">
        <span>Задерживать до моего решения</span>
        <Segmented
          label="Порог задержки"
          value={hold}
          onChange={save}
          options={HOLD.map((h) => ({ ...h }))}
        />
      </div>
      <p className="muted af-note">
        {hold === 'off'
          ? 'Сейчас не задерживается ни один лид.'
          : 'Задержанный лид не уходит ни в сеть, ни в Meta. Решение — в карточке лида: «Отправить в сеть» или «Не отправлять».'}
        {d && d.held > 0 && (
          <>
            {' '}
            <Link to="/leads" search={{ show: 'risky' }}>
              Ждут решения: {d.held}
            </Link>
          </>
        )}
      </p>
      {d && (
        <table className="af-tbl">
          <caption>Как сеть решила лиды за 30 дней</caption>
          <thead>
            <tr>
              <th>Риск</th>
              <th className="num">Лидов</th>
              <th className="num">Апрув</th>
              <th className="num">Мусор</th>
            </tr>
          </thead>
          <tbody>
            {d.levels.map((r) => {
              const decided = r.approved + r.rejected + r.trash;
              return (
                <tr key={r.level}>
                  <td>
                    {capFirst(FRAUD_LEVEL[r.level].label)}
                    <span className="sub">
                      {r.level === 'low'
                        ? `0–${d.thresholds.medium - 1}`
                        : r.level === 'medium'
                          ? `${d.thresholds.medium}–${d.thresholds.high - 1}`
                          : `от ${d.thresholds.high}`}
                    </span>
                  </td>
                  <td className="num">{r.leads}</td>
                  <td className="num">{share(r.approved, decided)}</td>
                  <td className="num">{share(r.trash, decided)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Card>
  );
}
